// Popover de notificações — abre ancorado ao sino do header. Lista as últimas
// atividades dos clubes do user (posts + pokes) e permite navegar pra fonte.
// Marca tudo como lido quando abre. Settings (config de push) ficam em link
// secundário no rodapé do popover.

import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';

export interface NotifItem {
  id: string;
  kind: 'post' | 'poke';
  postKind?: string;
  pokeType?: string;
  clubId: string;
  clubName: string;
  authorName: string;
  userId?: string;
  authorInitial: string;
  verb: string;          // pré-formatado pelo server ("compartilhou uma reflexão")
  message?: string | null;
  unread: boolean;
  createdAt: string;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export const NotifPopover: React.FC<{
  open: boolean;
  items: NotifItem[];
  loading: boolean;
  onClose: () => void;
  onItemClick: (item: NotifItem) => void;
  onOpenSettings: () => void;
  onOpenProfile?: (uid: string) => void;
}> = ({ open, items, loading, onClose, onItemClick, onOpenSettings, onOpenProfile }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    if (open) setVisible(true);
    else {
      // Espera animação fechar antes de remover do DOM
      const t = setTimeout(() => setVisible(false), 180);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) {
        // Não fecha se clicou no próprio sino (anchor) — pra evitar toggle conflito
        const target = e.target as HTMLElement;
        if (target.closest('[data-notif-anchor]')) return;
        onClose();
      }
    };
    // Pequeno delay pro evento de abrir não propagar pro doc handler
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onClose]);

  if (!visible) return null;

  return (
    <>
      {/* Backdrop transparente — captura tap fora pra fechar (mobile) */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 1500,
        background: 'transparent',
      }}/>
      <div ref={ref} style={{
        position: 'fixed', top: 92, right: 14, zIndex: 1501,
        width: 'calc(100vw - 28px)', maxWidth: 360,
        background: 'var(--paper)', borderRadius: 18,
        border: '1px solid var(--line)',
        boxShadow: '0 24px 48px -12px rgba(0,0,0,0.25), 0 8px 16px -8px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        opacity: open ? 1 : 0,
        transform: open ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.97)',
        transformOrigin: 'top right',
        transition: 'opacity 180ms ease, transform 180ms ease',
        maxHeight: '70vh',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 16px 10px',
          borderBottom: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div className="serif" style={{ fontSize: 18, lineHeight: 1, color: 'var(--ink)' }}>
            Atividade
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}>
            <Icon name="close" size={12} color="var(--ink-3)"/>
          </button>
        </div>

        {/* Lista (scroll) */}
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {loading && items.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
              Carregando…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🌱</div>
              <div className="serif" style={{ fontSize: 16, color: 'var(--ink-2)', fontStyle: 'italic' }}>
                Sem novidades por aqui.
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.4 }}>
                Atividade dos clubes que você participa aparece aqui.
              </div>
            </div>
          )}
          {items.map(item => (
            <button key={item.id} onClick={() => onItemClick(item)} style={{
              width: '100%', padding: '12px 16px',
              background: item.unread ? 'rgba(199,83,26,0.05)' : 'transparent',
              border: 'none', borderBottom: '1px solid var(--line)',
              display: 'flex', gap: 12, alignItems: 'flex-start',
              cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
              transition: 'background 150ms',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--paper-2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = item.unread ? 'rgba(199,83,26,0.05)' : 'transparent'; }}
            >
              {/* Avatar com iniciais — clicável → perfil do ator (se o item tiver userId) */}
              <div onClick={(e) => { if (onOpenProfile && item.userId) { e.stopPropagation(); onOpenProfile(item.userId); } }} style={{ position: 'relative', flexShrink: 0, cursor: (onOpenProfile && item.userId) ? 'pointer' : 'inherit' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: item.kind === 'poke' ? 'var(--ember)' : 'var(--ember-soft)',
                  color: item.kind === 'poke' ? 'var(--paper)' : 'var(--ember)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 600,
                }}>
                  {item.authorInitial}
                </div>
                {/* Dot de unread */}
                {item.unread && (
                  <div style={{
                    position: 'absolute', top: -2, right: -2,
                    width: 10, height: 10, borderRadius: '50%',
                    background: 'var(--ember)',
                    border: '2px solid var(--paper)',
                  }}/>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, lineHeight: 1.35, color: 'var(--ink)' }}>
                  <strong onClick={(e) => { if (onOpenProfile && item.userId) { e.stopPropagation(); onOpenProfile(item.userId); } }} style={{ fontWeight: 600, cursor: (onOpenProfile && item.userId) ? 'pointer' : 'inherit' }}>{item.authorName}</strong>{' '}
                  <span style={{ color: 'var(--ink-2)' }}>{item.verb}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.clubName} · {relativeTime(item.createdAt)}
                </div>
                {item.kind === 'poke' && item.message && (
                  <div style={{
                    fontSize: 12, color: 'var(--ink-2)', marginTop: 6,
                    fontStyle: 'italic',
                    background: 'var(--paper-2)', padding: '6px 10px', borderRadius: 8,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    "{item.message}"
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Footer com link pra config de push */}
        <button onClick={onOpenSettings} style={{
          width: '100%', padding: '12px 16px',
          background: 'var(--paper-2)', border: 'none',
          borderTop: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            Configurar lembretes e notificações
          </span>
          <Icon name="chevron-right" size={12} color="var(--ink-3)"/>
        </button>
      </div>
    </>
  );
};
